import React, { useCallback, useEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import {
	ImperativePanelGroupHandle,
	PanelContent,
	PanelGroup,
	ResizableHandle,
	useMinSize,
	ImperativePanelHandle
} from '@bringg-frontend/resizable-panel';
import { InfiniteLoader } from 'react-virtualized';
import { usePrevious } from '@bringg-frontend/hooks';
import { Spinner } from '@bringg/react-components';

import { Panels, calcSectionsSizes, defaultSizes } from './utils/resizable-panels';
import useOrderExplorerTranslations from './use-order-explorer-translations';
import TopSection from './components/top-section/top-section';
import OrderList from './components/order-list';
import useOrderExplorerStore from './stores/use-order-explorer-store';
import OrdersMap from './components/orders-map';
import { orderExplorerRootStore } from './stores/order-explorer-root-store';
import { loaderPageCreator } from '../shared/data/loader-page-creator';
import OrdersDetails from './components/orders-details/orders-details';
import { orderExplorerPageLoader } from 'bringg-web/features/monitoring/order-explorer/data/order-explorer-page-loader';
import useMonitoringStore from '../shared/hooks/use-monitoring-store';

import styles from './order-explorer.module.scss';

const OrderExplorer = () => {
	const infiniteLoaderRef = useRef<InfiniteLoader>();
	const groupPanelRef = useRef<ImperativePanelGroupHandle>(null);
	const detailsPanelRef = useRef<ImperativePanelHandle>(null);
	const minSize = useMinSize(24, '[data-panel-group-id="order-explorer"]');
	const [sizes, setSizes] = useState(defaultSizes);
	const [mainColumnScroll, setMainColumnScroll] = useState(false);

	const { details, map, loading } = useOrderExplorerTranslations();

	const layoutChange = useCallback(
		(panel: Panels, collapse: boolean) => {
			const sizesToSet = calcSectionsSizes(sizes, panel, collapse);

			groupPanelRef.current.setLayout(sizesToSet);
		},
		[sizes]
	);

	const { initDataLoaded } = useMonitoringStore();
	const { orderExplorerStore, ordersMapStore } = useOrderExplorerStore();

	useEffect(() => {
		return () => {
			orderExplorerStore.resetScroll();
			orderExplorerRootStore.clearStore();
		};
	}, []);

	const previousTotal = usePrevious(orderExplorerStore.currentTableDefinition?.total);

	useEffect(() => {
		if (
			!orderExplorerStore.initialLoading &&
			(previousTotal === 0 || previousTotal === 1) &&
			orderExplorerStore.currentTableDefinition?.hasMoreData
		) {
			infiniteLoaderRef.current?.resetLoadMoreRowsCache(true);
		}
	}, [
		infiniteLoaderRef,
		previousTotal,
		orderExplorerStore.initialLoading,
		orderExplorerStore.currentTableDefinition?.hasMoreData
	]);

	const prevCheckedRoutesSize = usePrevious(ordersMapStore.markersToViewOnMap.size);

	useEffect(() => {
		if (prevCheckedRoutesSize === 0 && ordersMapStore.markersToViewOnMap.size && sizes[Panels.MAP] === 0) {
			const sizesToSet = calcSectionsSizes(sizes, Panels.MAP, false);
			groupPanelRef.current.setLayout(sizesToSet);
		}
	}, [ordersMapStore.markersToViewOnMap.size, sizes, prevCheckedRoutesSize]);

	const onLayoutChange = (_sizes: number[]) => {
		setSizes(_sizes);
		setMainColumnScroll(_sizes[Panels.ORDERS_DETAILS] === 0);
	};

	const onDetailsColumnsChange = (sizeRatioChange: number) => {
		if (sizes[Panels.ORDERS_DETAILS] !== 80) {
			detailsPanelRef.current.resize(detailsPanelRef.current.getSize() * sizeRatioChange);
		}
	};

	return (
		<div data-test-id="order-explorer" className={styles.orderExplorer}>
			{initDataLoaded && <TopSection />}
			{orderExplorerStore.currentTableDefinition && (
				<div data-test-id="order-explorer-container" className={styles.orderExplorerMain}>
					<Spinner tip={loading} spinning={orderExplorerStore.initialLoading} className={styles.spinner}>
						<PanelGroup
							direction="horizontal"
							className={styles.panelGroup}
							id="order-explorer"
							disablePointerEventsDuringResize={true}
							onLayout={onLayoutChange}
							ref={groupPanelRef}
						>
							<InfiniteLoader
								ref={infiniteLoaderRef}
								isRowLoaded={({ index }) => orderExplorerStore.isLoaded(index)}
								loadMoreRows={async () => orderExplorerPageLoader.load()}
								rowCount={orderExplorerStore.currentTableDefinition.total}
								minimumBatchSize={loaderPageCreator.PAGE_SIZE}
								threshold={100}
							>
								{({ onRowsRendered, registerChild }) => (
									<PanelContent collapsible={false} minSize={10} defaultSize={sizes[Panels.ORDERS]}>
										<OrderList
											onRowsRendered={onRowsRendered}
											registerChild={registerChild}
											withScroll={mainColumnScroll}
										/>
									</PanelContent>
								)}
							</InfiniteLoader>
							<ResizableHandle />
							<PanelContent
								sectionName={details}
								defaultSize={sizes[Panels.ORDERS_DETAILS]}
								minSize={minSize}
								onPanelCollapse={(collapse: boolean) => layoutChange(Panels.ORDERS_DETAILS, collapse)}
								ref={detailsPanelRef}
							>
								<OrdersDetails onColumnsChange={onDetailsColumnsChange} />
							</PanelContent>
							<ResizableHandle />
							<PanelContent
								sectionName={map}
								defaultSize={sizes[Panels.MAP]}
								minSize={minSize}
								onPanelCollapse={(collapse: boolean) => layoutChange(Panels.MAP, collapse)}
							>
								<OrdersMap />
							</PanelContent>
						</PanelGroup>
					</Spinner>
				</div>
			)}
		</div>
	);
};

export default observer(OrderExplorer);
